import React, { FC, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { EditIcon } from '../../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Switch } from '../../../../components/switch/index';
import { useFormat, useModalState } from '../../../../hooks/index';
import { DilutionCondition } from '../../../../types/pool-plans.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { RecordAttachedFilesPreview } from '../../components/record-attached-files-preview';
import { TerminationModal } from '../../termination-modal';
import { ChildrenFormProps, EditShareClassStepVariant } from '../pool-form.types';

export type EditPreviewProps = {
  handleTerminate: (date: Date) => void;
  onEditClick: (step: EditShareClassStepVariant) => void;
  previewMode?: true;
} & ChildrenFormProps;

export const EditPreview: FC<EditPreviewProps> = ({
  handleCloseModal,
  formData,
  onEditClick,
  handleTerminate,
  previewMode,
  isTerminated,
  terminationDate,
  companyId: _companyId,
  docusignFileLinks,
}) => {
  const [terminatingData, setTerminatingData] = useState<{
    date: Date | null;
    isTerminating: boolean;
  } | null>(null);
  const { stepOne, stepTwo } = formData();
  const { format } = useFormat();
  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };
  const {
    toggler: terminationModalToggler,
    isOpen: isOpenTerminationModal,
    handleOpenModal: handleOpenTerminationModal,
    handleCloseModal: handleCloseTerminationModal,
    handleSuccessModal: handleSuccessTerminationModalModal,
  } = useModalState({
    onSuccess: () => {
      if (!terminatingData?.date) return;
      handleTerminate(terminatingData.date);
      handleCloseModal();
    },
  });
  return (
    <>
      <AlertDialogWrapper
        control={{ onOpenChange: terminationModalToggler, open: isOpenTerminationModal }}
      >
        <TerminationModal
          onClose={handleCloseTerminationModal}
          onSuccess={handleSuccessTerminationModalModal}
          text="pool"
        />
      </AlertDialogWrapper>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <div className="flex w-full justify-between px-4">
            <span className="text-sm font-[550] text-gray-700">
              <AppFormattedMessage id="GENERAL" />
            </span>
            {!previewMode && (
              <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                <EditIcon iconColor="#2565C8" />
              </Button>
            )}
          </div>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="POOL_NAME" />}
              value={stepOne?.name}
            />

            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="FULLY_DILUTED_SHARES" />}
              value={
                stepOne?.dillutedSharesCount &&
                Number(stepOne?.dillutedSharesCount).toLocaleString('en-US')
              }
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="SHARE_CLASS" />}
              value={stepOne?.shareClass?.name || '-'}
            />
            <SummaryItem
              className="py-3"
              title={<AppFormattedMessage id="DATE" />}
              value={stepOne?.date ? format(stepOne?.date, 'dd/MM/yyyy') : '-'}
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="DILUTION_CONDITION" />}
              value={
                stepOne?.dilutionCondition === DilutionCondition.INCLUDE_CAP_TABLE ? (
                  <AppFormattedMessage id="INCLUDE_CAP_TABLE" />
                ) : (
                  <AppFormattedMessage id="EXCLUDE_CAP_TABLE" />
                )
              }
            />
          </div>
        </div>
        <div className="flex w-full min-w-0 items-start justify-between overflow-hidden rounded-lg border border-gray-100 bg-white px-4">
          <div className="flex min-w-0 flex-1 overflow-hidden">
            {(docusignFileLinks?.length ?? 0) > 0 ? (
              <RecordAttachedFilesPreview
                docusignFileLinks={docusignFileLinks}
                files={stepTwo?.files}
                onDocumentClick={handleDocumentClick}
                previewMode={previewMode}
              />
            ) : (
              <div className="flex min-w-0 flex-1 gap-3 py-2">
                <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
                  <AppFormattedMessage id="DOCUMENTS" />:
                </span>
                <RecordAttachedFilesPreview
                  files={stepTwo?.files}
                  onDocumentClick={handleDocumentClick}
                  previewMode={previewMode}
                />
              </div>
            )}
          </div>
          {!previewMode && (
            <Button className="w-fit shrink-0 pt-2" onClick={() => onEditClick(2)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>
        <div className="flex w-full min-w-0 items-start justify-between overflow-hidden rounded-lg border border-gray-100 bg-white px-4 py-2">
          <div className="flex min-w-0 flex-1 gap-3 overflow-hidden">
            <span className="w-[120px] shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div className="min-w-0 flex-1 break-words text-xs font-[450] text-gray-700">
              {stepTwo?.additionalNotes || '-'}
            </div>
          </div>
          {!previewMode && (
            <Button className="w-fit shrink-0" onClick={() => onEditClick(2)} styleType="NONE">
              <EditIcon iconColor="#2565C8" />
            </Button>
          )}
        </div>

        {!previewMode && !isTerminated && (
          <div className="flex w-full flex-col justify-center rounded-lg bg-fireside-25 px-1 py-1">
            <div className="flex gap-2 px-3 py-3">
              <Switch
                onChange={(state) =>
                  setTerminatingData(state ? { isTerminating: state, date: null } : null)
                }
              />
              <span className="text-sm font-[550] text-fireside-600">
                <AppFormattedMessage id="TERMINATE_POOL" />
              </span>
            </div>
            {terminatingData && (
              <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
                <FormDatePicker
                  calendar={{ fromDate: new Date() }}
                  inputValue={terminatingData.date ? format(terminatingData.date, 'dd/MM/yy') : ''}
                  inputWrapperClassName="cursor-pointer"
                  onSelect={(date) => setTerminatingData({ isTerminating: true, date })}
                  placeholder={<AppFormattedMessage id="DATE_OF_TERMINATION" />}
                  value={terminatingData?.date}
                />
                <DropDown
                  answer="Plan termination refers to the process of ending an employee stock option plan, stock purchase plan, or any other equity compensation plan."
                  className="rounded-lg bg-gray-50"
                  question={<AppFormattedMessage id="POOL_TERMINATION_WORKS" />}
                />
              </div>
            )}
          </div>
        )}
        {!previewMode && isTerminated && terminationDate && (
          <div className="flex flex-col gap-3 p-3 pt-5">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-[550] text-fireside-600">
                <AppFormattedMessage id="TERMINATE_POOL" />
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs font-[450] text-gray-700">
                  <AppFormattedMessage id="CURRENT_TERMINATION_DATE" />:
                </span>
                <span className="text-xs font-[450] text-fireside-600">
                  {terminationDate ? format(terminationDate, 'dd/MM/yy') : ''}
                </span>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
              <FormDatePicker
                calendar={{ fromDate: new Date() }}
                inputValue={terminatingData?.date ? format(terminatingData.date, 'dd/MM/yy') : ''}
                inputWrapperClassName="cursor-pointer"
                onSelect={(date) => setTerminatingData({ isTerminating: true, date })}
                placeholder={<AppFormattedMessage id="NEW_TERMINATION_DATE" />}
                value={terminatingData?.date}
              />
              <DropDown
                answer="Pool termination refers to the process of ending an employee stock option plan, stock purchase plan, or any other equity compensation plan."
                className="rounded-lg bg-gray-50"
                question={<AppFormattedMessage id="POOL_TERMINATION_WORKS" />}
              />
            </div>
          </div>
        )}
      </div>

      <div
        className={twMerge(
          'flex h-9 w-full justify-start gap-3 px-6',
          previewMode && 'justify-end',
          terminatingData && 'justify-between',
        )}
      >
        {terminatingData && (
          <Button
            className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
            onClick={handleCloseModal}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
        )}
        <Button
          className={twMerge(
            'w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700',
            terminatingData && 'bg-fireside-600 text-white',
            previewMode && 'bg-brand-700 text-white',
          )}
          disabled={(terminatingData && !terminatingData.date) || false}
          onClick={() => {
            if (!terminatingData) return handleCloseModal();

            terminatingData.date && handleOpenTerminationModal();
          }}
          styleType="NONE"
          type="button"
        >
          {terminatingData ? (
            <AppFormattedMessage id="TERMINATE_POOL" />
          ) : previewMode ? (
            <AppFormattedMessage id="CLOSE" />
          ) : (
            <AppFormattedMessage id="CANCEL" />
          )}
        </Button>
      </div>
    </>
  );
};
